import { useAuth } from "@/hooks/useAuth";
import { useUserCommunity } from "@/hooks/useUserCommunity";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "public_video_pool_community_override_id";

export type PublicVideoPoolCommunityContextValue = {
  /** Gesetzte Community-UUID für den öffentlichen Community-Pool; null = Profil-Community */
  overrideCommunityId: string | null;
  setOverrideCommunityId: (id: string | null) => void;
  /** Effektive Community für Abfragen: Override oder Profil-Community */
  effectiveCommunityId: string | null;
};

const PublicVideoPoolCommunityContext =
  createContext<PublicVideoPoolCommunityContextValue | null>(null);

export function PublicVideoPoolCommunityFilterProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { user } = useAuth();
  const { data: userCommunity } = useUserCommunity(user?.id ?? null);

  const [overrideCommunityId, setOverrideCommunityIdState] = useState<
    string | null
  >(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw || raw === "null" || raw === "") return null;
      return raw;
    } catch {
      return null;
    }
  });

  const setOverrideCommunityId = useCallback((id: string | null) => {
    setOverrideCommunityIdState(id);
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const effectiveCommunityId =
    overrideCommunityId ?? userCommunity?.id ?? null;

  const value = useMemo(
    () => ({
      overrideCommunityId,
      setOverrideCommunityId,
      effectiveCommunityId,
    }),
    [overrideCommunityId, setOverrideCommunityId, effectiveCommunityId]
  );

  return (
    <PublicVideoPoolCommunityContext.Provider value={value}>
      {children}
    </PublicVideoPoolCommunityContext.Provider>
  );
}

export function usePublicVideoPoolCommunityFilter(): PublicVideoPoolCommunityContextValue {
  const ctx = useContext(PublicVideoPoolCommunityContext);
  if (!ctx) {
    throw new Error(
      "usePublicVideoPoolCommunityFilter nur innerhalb von PublicVideoPoolCommunityFilterProvider"
    );
  }
  return ctx;
}

export function usePublicVideoPoolCommunityFilterOptional(): PublicVideoPoolCommunityContextValue | null {
  return useContext(PublicVideoPoolCommunityContext);
}
